import React from 'react';

import { Icon, Text } from '@/nova/components';

import * as S from './NewVersionLink.styles';

export type NewVersionLinkProps = {
  /** How many times a locked field was clicked (from useLockedNudge): each new value replays the shake once; 0 shows
   *  the link still. */
  nudge: number;
  /** Start a new (unlocked) version from the selected one. */
  onClick: () => void;
  disabled?: boolean;
  className?: string;
};

/**
 * "+ New version from this": the one action a composer's rail foot offers in place of Generate while a generated
 * (locked) version is selected. Its rail is disabled meanwhile; a click on a disabled field shakes this link (see
 * useLockedNudge and lockedRailPassThrough). Shared by every marketing asset's composer.
 */
export const NewVersionLink = ({
  nudge,
  onClick,
  disabled = false,
  className,
}: NewVersionLinkProps): React.ReactElement => (
  // The key changes with each nudge, so the one-shot shake replays on every locked-field click.
  <S.Link key={nudge} $shake={nudge > 0} type="button" className={className} disabled={disabled} onClick={onClick}>
    <Icon name="plus" size={15} />
    <Text tag="span" text="New version from this" />
  </S.Link>
);

/** What useLockedNudge hands a composer. */
export type LockedNudge = {
  /** The NewVersionLink's `nudge`. */
  nudge: number;
  /** The rail container's onClick: undefined while unlocked. A click on an enabled control (a section header, a
   *  link) is not a nudge. */
  onRailClick: ((event: React.MouseEvent) => void) | undefined;
};

/** Clicks on these stay theirs: they work on a locked version too (section headers, links, live inputs). */
const LIVE_CONTROL = 'button:enabled, input:enabled, select:enabled, textarea:enabled, a[href]';

/**
 * The locked-rail nudge: while `locked`, a click that falls through a disabled field to the rail container bumps
 * `nudge` (the NewVersionLink then shakes). Resets to 0 whenever `resetKey` changes (the selected version: a fork, a
 * switch, a re-lock after a run), so the shake replays only on an actual locked-field click, never when the link
 * remounts on another version.
 */
export const useLockedNudge = (locked: boolean, resetKey: unknown): LockedNudge => {
  const [nudge, setNudge] = React.useState(0);
  React.useEffect(() => {
    setNudge(0);
  }, [resetKey]);
  const onRailClick = locked
    ? (event: React.MouseEvent): void => {
        if (event.target instanceof Element && event.target.closest(LIVE_CONTROL)) return;
        setNudge((n) => n + 1);
      }
    : undefined;
  return { nudge, onRailClick };
};
